import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./kycPreview.module.css";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Button,
  Link as MuiLink,
  Card,
  CardContent,
  Grid2,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import BackLink from "../../components/BackLink/BackLink";
import AccordionSection from "../../components/AccordionSection/AccordionSection";
import { renderPdfFirstPageToDataUrl } from "../../utils/pdfThumb";
import { CircularProgress } from "@mui/material";
import Config from "../../service/Config";

const API_BASE = Config.kyc_api_endpoint;
const UPLOADS_BASE =
  import.meta.env?.VITE_UPLOADS_BASE_URL || "https://kyc.getcupi.com/files";
const FILES_BASE =
  import.meta.env?.VITE_KYC_FILES_BASE_URL ||
  "https://kyc.getcupi.com/files/kyc";

/* ------------------------------------------------------------------
 * Helper: get valid Cognito tokens from localStorage
 * ------------------------------------------------------------------ */
function getAuthHeaders() {
  const tokens = localStorage.getItem("authToken");
  if (!tokens) return null;

  try {
    const parsed = JSON.parse(tokens);
    if (!parsed?.IdToken || !parsed?.AccessToken) return null;

    return {
      "Content-Type": "application/json",
      "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
      aws_cognito_id_token: parsed.IdToken,
      aws_cognito_access_token: parsed.AccessToken,
    };
  } catch {
    return null;
  }
}

const KycPreview = ({ onApprove = () => {}, onReject = () => {} }) => {
  const { companyId } = useParams();
  const { entityType } = useParams();
  const navigate = useNavigate();
  const decodedEntityType = decodeURIComponent(entityType || "");

  const [view, setView] = useState({
    poc: {},
    company: {},
    authorized: { documents: [] },
    digilocker: {},
    imageKyc: {},
    status: "",
    reject_reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [savingDecision, setSavingDecision] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  const api = axios.create({ baseURL: API_BASE, timeout: 15000 });

  /* ----------------------------------------------------------------
   * Fetch for normal entity types
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (decodedEntityType === "Others") return;
    if (!companyId) return;

    const controller = new AbortController();
    let ignore = false;

    (async () => {
      setLoading(true);
      setError("");

      const headers = getAuthHeaders();
      if (!headers) {
        navigate("/");
        return;
      }

      try {
        const payload = { company_id: companyId };

        const [usersRes, basicRes, advRes, advDigi] = await Promise.all([
          api.post("/users/by-company", payload, {
            signal: controller.signal,
            headers,
            validateStatus: () => true,
          }),
          api.post("/basic-kyc/by-company", payload, {
            signal: controller.signal,
            headers,
            validateStatus: () => true,
          }),
          api.post("/advanced-kyc/data", payload, {
            signal: controller.signal,
            headers,
            validateStatus: () => true,
          }),
          api.post("/advanced-kyc/by-company", payload, {
            signal: controller.signal,
            headers,
            validateStatus: () => true,
          }),
        ]);

        if (ignore) return;

        // Users
        if (!(usersRes.status >= 200 && usersRes.status < 300)) {
          throw new Error(usersRes.data?.error || "Users request failed");
        }
        const u = usersRes?.data?.users?.[0] || {};

        // Basic KYC
        const companyLatest =
          basicRes.status === 404
            ? {}
            : basicRes.status >= 200 && basicRes.status < 300
            ? basicRes.data || {}
            : (() => {
                throw new Error(
                  basicRes.data?.error || "Basic KYC request failed"
                );
              })();

        // Advanced status
        const advancedData =
          advRes.status === 404
            ? {}
            : advRes.status >= 200 && advRes.status < 300
            ? advRes.data?.data || {}
            : (() => {
                throw new Error(
                  advRes.data?.error || "Advanced KYC request failed"
                );
              })();

        // Provider response
        const pr = advDigi?.data?.latest?.provider_response || {};
        const actions = Array.isArray(pr.actions) ? pr.actions : [];
        const dlAction = actions.find((a) => a.action_ref === "digilocker-1");

        const aadhaarPdfUrl = pr.aadhaar_link || null;
        const panFileUrl = pr.pan_link || null;
        const imageUrl = pr.image_link || null;

        // Make a first-page thumbnail for Aadhaar PDF
        let aadhaarThumb = null;
        try {
          if (aadhaarPdfUrl) {
            aadhaarThumb = await renderPdfFirstPageToDataUrl(aadhaarPdfUrl);
          }
        } catch (_) {
          aadhaarThumb = null;
        }

        const aadhaarMeta = dlAction?.details?.aadhaar || {};
        const panMeta = dlAction?.details?.pan || {};

        // Authorized docs
        const latest = advDigi?.data?.latest || {};
        const authDocs = Array.isArray(latest?.documents)
          ? latest.documents
          : latest?.document
          ? [latest.document]
          : [];

        let panThumb = null;
        try {
          if (panFileUrl) {
            panThumb = await renderPdfFirstPageToDataUrl(panFileUrl);
          }
        } catch (_) {
          panThumb = null;
        }

        setView((prev) => ({
          ...prev,
          poc: {
            name: u.name || "-",
            phone_number: u.phone_number || "-",
            email: u.email || "-",
          },
          company: {
            company_name: companyLatest?.data?.Company_Name || "-",
            gstin: companyLatest?.data?.GSTIN || "-",
            pan:
              companyLatest?.data?.PAN ||
              (companyLatest?.data?.GSTIN &&
              companyLatest?.data?.GSTIN.length >= 15
                ? companyLatest?.data?.GSTIN.slice(2, -3)
                : "-"),
            cin: companyLatest?.data?.CIN || "-",
            entity_type: companyLatest?.data?.Business_Entity_Type || "-",
            registered_address:
              companyLatest?.data?.Registered_Address || "-",
          },
          authorized: {
            name: latest?.name || "-",
            designation: latest?.designation || "-",
            phone: latest?.phone || "-",
            documents: authDocs,
          },
          digilocker: {
            customer_name: pr.customer_name || null,
            aadhaar: {
              file_url: aadhaarPdfUrl,
              thumb_url: aadhaarThumb,
              gender: aadhaarMeta.gender || null,
              dob: aadhaarMeta.dob || aadhaarMeta?.dob_string || null,
              current_address: aadhaarMeta.current_address || null,
              permanent_address: aadhaarMeta.permanent_address || null,
              name: aadhaarMeta.name || null,
              id_number: aadhaarMeta.id_number || null,
            },
            pan: {
              file_url: panFileUrl,
              thumb_url: panThumb,
              name: panMeta.name || null,
              gender: panMeta.gender || null,
              dob: panMeta.dob || null,
              id_number: panMeta.id_number || null,
            },
          },
          imageKyc: {
            image_url: imageUrl,
          },
          status: advancedData?.status || prev.status,
          reject_reason: advRes?.data?.data?.rejectReason || "",
        }));
      } catch (e) {
        if (axios.isCancel?.(e) || e.name === "CanceledError") return;
        if (!ignore)
          setError(
            e?.response?.data?.error || e.message || "Something went wrong"
          );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [companyId, decodedEntityType, navigate]);

  /* ----------------------------------------------------------------
   * Fetch for "Others" entity type
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (decodedEntityType !== "Others") return;
    if (!companyId) return;

    const fetchOthers = async () => {
      setLoading(true);
      setError("");

      const headers = getAuthHeaders();
      if (!headers) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(
          `${Config.kyc_api_endpoint}/others/${companyId}`,
          { headers }
        );
        const json = await res.json();

        if (json.success) {
          setView(json.data);
        } else {
          setError(json.message || "Failed to fetch data");
        }
      } catch (e) {
        setError(e.message || "Fetch Others API failed");
      } finally {
        setLoading(false);
      }
    };

    fetchOthers();
  }, [decodedEntityType, companyId, navigate]);

  const { poc, company, authorized, digilocker, imageKyc } = view;

  function openRejectDialog() {
    setReasonError("");
    setRejectReason("");
    setRejectOpen(true);
  }
  function closeRejectDialog() {
    if (!savingDecision) setRejectOpen(false);
  }

  async function handleDecision(decision, reason) {
    const headers = getAuthHeaders();
    if (!headers) {
      navigate("/");
      return;
    }

    try {
      setSavingDecision(true);
      await api.post(
        "/advanced-kyc/decision",
        {
          company_id: companyId,
          status: decision,
          ...(decision === "rejected" ? { reason } : {}),
        },
        {
          headers,
          validateStatus: () => true,
        }
      );

      setView((prev) => ({
        ...prev,
        status: decision,
        review_reason: decision === "rejected" ? reason : null,
      }));
      setSavingDecision(false);
      if (decision === "rejected") closeRejectDialog();
    } catch (e) {
      setSavingDecision(false);
      alert(e?.response?.data?.error || e.message || "Update failed");
    }
  }

  async function confirmReject() {
    const rsn = (rejectReason || "").trim();
    if (rsn.length < 3) {
      setReasonError("Please provide a reason (min 3 characters).");
      return;
    }
    await handleDecision("rejected", rsn);
  }

  function openApproveDialog() {
    setApproveOpen(true);
  }
  function closeApproveDialog() {
    if (!savingDecision) setApproveOpen(false);
  }
  async function confirmApprove() {
    await handleDecision("approved");
    closeApproveDialog();
  }

  const authDocs =
    Array.isArray(authorized?.documents) && authorized.documents.length
      ? authorized.documents
      : [];

  const status = (view?.status || "").toLowerCase();

  return (
    <div className={`${styles.wrapper} container`}>
      <BackLink />
      {savingDecision && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2100,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#2348AB" }} />
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#2348AB" }} />
        </div>
      )}

      {error && (
        <div style={{ padding: 8, color: "#B00020" }}>Error: {error}</div>
      )}

      {decodedEntityType !== "Others" ? (
        <>
          <AccordionSection title="POC details" defaultExpanded>
            <hr />
            <Grid2 spacing={0.5} sx={{ mb: 2, mt: 1 }}>
              <DetailRow label="Name" value={poc.name} />
              <DetailRow label="Phone no" value={poc.phone_number} />
              <DetailRow label="Email ID" value={poc.email} />
            </Grid2>
          </AccordionSection>

          <Stack alignItems="center" textAlign="center" spacing={0.75}>
            {status === "approved" && (
              <Typography sx={{ color: "#198754", fontSize: "24px" }}>
                Approved
              </Typography>
            )}

            {status === "rejected" && (
              <>
                <Typography sx={{ color: "#DC3545", fontSize: "24px" }}>
                  Rejected
                </Typography>
                {view?.reject_reason && (
                  <Typography>
                    <Box component="span" sx={{ color: "#4C4E64", mr: 0.75 }}>
                      Reason for rejection:
                    </Box>
                    <Box component="span">{view.reject_reason}</Box>
                  </Typography>
                )}
              </>
            )}
          </Stack>

          <AccordionSection title="Company details" defaultExpanded>
            <hr />
            <Grid2 spacing={0.5} sx={{ mb: 2, mt: 1 }}>
              <DetailRow
                label="Name of the company"
                value={company.company_name}
              />
              <DetailRow label="GSTIN" value={company.gstin} />
              <DetailRow label="PAN" value={company.pan} />
              <DetailRow label="CIN" value={company.cin} />
              <DetailRow
                label="Business Entity Type"
                value={company.entity_type}
              />
              <DetailRow label="Address" value={company.registered_address} />
            </Grid2>
          </AccordionSection>

          <AccordionSection
            title={`${authorized.designation} details`}
            defaultExpanded
          >
            <hr />
            <Grid2 spacing={0.5} sx={{ mb: 2, mt: 1 }}>
              <DetailRow label="Name" value={authorized?.name} />
              <DetailRow label="Designation" value={authorized?.designation} />
              <DetailRow label="Phone no" value={authorized?.phone} />
              <DetailRow
                label="Supporting Documents"
                value={<DocsList docs={authDocs} />}
              />
            </Grid2>
          </AccordionSection>
        </>
      ) : (
        <>
          <AccordionSection title="POC details" defaultExpanded>
            <hr />
            <Grid2 spacing={0.5} sx={{ mb: 2, mt: 1 }}>
              <DetailRow label="Name" value={view.name} />
              <DetailRow label="Phone no" value={view?.phone_no} />
              <DetailRow label="Email ID" value={view?.email} />
            </Grid2>
          </AccordionSection>
          <AccordionSection title="Company details" defaultExpanded>
            <hr />
            <Grid2 spacing={0.5} sx={{ mb: 2, mt: 1 }}>
              <DetailRow
                label="Name of the company"
                value={view?.company_name}
              />
              <DetailRow label="PAN" value={view?.pan} />
              <DetailRow
                label="Business Entity Type"
                value={view?.entity_type}
              />
              <DetailRow label="Address" value={view?.address} />
            </Grid2>
          </AccordionSection>
        </>
      )}

      {digilocker?.aadhaar?.gender?.length > 0 && (
        <AccordionSection title="Digi locker details" defaultExpanded>
          <hr />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              e-Aadhaar
            </Typography>
            <DocBlock
              title=""
              meta={[
                [
                  "Name",
                  digilocker?.aadhaar?.name || digilocker?.customer_name,
                ],
                ["Gender", digilocker?.aadhaar?.gender],
                ["Date of Birth", digilocker?.aadhaar?.dob],
                ["Current Address", digilocker?.aadhaar?.current_address],
                ["Permanent Address", digilocker?.aadhaar?.permanent_address],
              ]}
              fileUrl={digilocker?.aadhaar?.file_url}
              thumbUrl={digilocker?.aadhaar?.thumb_url}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              PAN
            </Typography>
            <DocBlock
              title=""
              meta={[
                ["Name", digilocker?.pan?.name],
                ["Gender", digilocker?.pan?.gender],
                ["Date of Birth", digilocker?.pan?.dob],
              ]}
              fileUrl={digilocker?.pan?.file_url}
              thumbUrl={digilocker?.pan?.thumb_url}
            />
          </Box>
        </AccordionSection>
      )}

      {imageKyc?.image_url?.length > 0 && (
        <AccordionSection title="KYC Image" defaultExpanded>
          <hr />
          <Card variant="outlined" sx={{ borderRadius: 3, mt: 1, mb: 1 }}>
            <CardContent>
              <Stack spacing={2}>
                <img style={{ width: "20%" }} src={imageKyc.image_url} alt="KYC" />
              </Stack>
            </CardContent>
          </Card>
        </AccordionSection>
      )}

      {view?.status === "pending_approval" && (
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={openRejectDialog}
            sx={{
              backgroundColor: "#B20A00",
              "&:hover": { backgroundColor: "#B71C1C" },
              minWidth: 200,
              borderRadius: "8px",
            }}
            disabled={savingDecision}
          >
            {savingDecision ? "Saving…" : "Reject"}
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={openApproveDialog}
            sx={{
              backgroundColor: "#198754",
              "&:hover": { backgroundColor: "#198754" },
              minWidth: 200,
              borderRadius: "8px",
            }}
            disabled={savingDecision}
          >
            {savingDecision ? "Saving…" : "Approve"}
          </Button>
        </Stack>
      )}

      {/* Reject Dialog */}
      <Dialog
        open={rejectOpen}
        onClose={closeRejectDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 500, color: "#2348AB" }}>
          Please provide a reason for rejection.
        </DialogTitle>

        <DialogContent>
          <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder="Type the reason..."
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              if (reasonError) setReasonError("");
            }}
            error={!!reasonError}
            helperText={reasonError || " "}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeRejectDialog}
            disabled={savingDecision}
            sx={{
              textTransform: "none",
              backgroundColor: "#FFFFFF",
              color: "#2348AB",
              border: "2px solid #2348AB",
              width: "50%",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmReject}
            disabled={savingDecision}
            sx={{
              backgroundColor: "#2348AB",
              color: "#FFFFFF",
              textTransform: "none",
              width: "50%",
            }}
          >
            {savingDecision ? "Saving…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog
        open={approveOpen}
        onClose={closeApproveDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{ color: "#2348AB", fontWeight: 500, fontSize: "24px" }}
          >
            Are you sure you want to approve this KYC?
          </Typography>
          <Box
            component="img"
            src="/approve.svg"
            alt="Approve illustration"
            sx={{ display: "block", maxWidth: 160, width: "100%" }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeApproveDialog}
            disabled={savingDecision}
            sx={{
              textTransform: "none",
              backgroundColor: "#FFFFFF",
              color: "#2348AB",
              border: "2px solid #2348AB",
              width: "50%",
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={confirmApprove}
            disabled={savingDecision}
            sx={{
              backgroundColor: "#2348AB",
              color: "#FFFFFF",
              textTransform: "none",
              width: "50%",
            }}
          >
            {savingDecision ? "Saving…" : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default KycPreview;

/* ---------- helpers ---------- */

function DetailRow({ label, value }) {
  const isPrimitive =
    typeof value === "string" || typeof value === "number" || value == null;
  return (
    <Grid2 item xs={12}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "140px 8px 1fr", sm: "180px 8px 1fr" },
          alignItems: "baseline",
          columnGap: 1,
          py: 1.25,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "#4C4E64",
            fontWeight: 600,
            whiteSpace: "nowrap",
            fontSize: "16px",
          }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: "#4C4E64", fontWeight: 600 }}>
          :
        </Typography>
        {isPrimitive ? (
          <Typography variant="body1">{value || "-"}</Typography>
        ) : (
          <Box>{value}</Box>
        )}
      </Box>
    </Grid2>
  );
}

function DocBlock({ title, meta = [], fileUrl, thumbUrl }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 220px" },
        gap: 2,
        my: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        <Grid2 spacing={0.5}>
          {meta.map(([k, v]) => (
            <DetailRow key={k} label={k} value={v} />
          ))}
          <DetailRow
            label="Document"
            value={
              fileUrl ? <OpenButton href={fileUrl} label="Download" /> : "—"
            }
          />
        </Grid2>
      </Box>

      {thumbUrl && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <img
            src={thumbUrl}
            alt={`${title} preview`}
            style={{
              width: 180,
              height: "auto",
              border: "1px solid #eee",
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
        </Box>
      )}
    </Box>
  );
}

const basename = (p = "") => p.split(/[\\/]/).pop() || "";

function resolveDocHref(doc) {
  if (!doc) return null;
  if (typeof doc === "string") return doc;

  if (doc.public_url) return doc.public_url;

  const name =
    doc.stored_filename || doc.document_id || basename(doc.path) || "";

  if (!name) return null;

  if (/_AADHAAR\.pdf$|_PAN\.pdf$|_VIDEO\.mp4$/i.test(name)) {
    return `${FILES_BASE}/${encodeURIComponent(name)}`;
  }

  return `${UPLOADS_BASE}/${encodeURIComponent(name)}`;
}

function DocsList({ docs = [] }) {
  if (!Array.isArray(docs) || docs.length === 0) return <>—</>;
  return (
    <Box sx={{ display: "grid", gap: 0.5 }}>
      {docs.map((d, i) => {
        const key =
          d?.document_id || d?.stored_filename || d?.filename || `doc-${i}`;
        const href = resolveDocHref(d);
        const label =
          d?.filename || d?.stored_filename || d?.document_id || "Document";
        return (
          <Box
            key={key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: "1px solid #E3E3E3",
              borderRadius: 1,
              p: 1,
              width: "fit-content",
              maxWidth: "100%",
            }}
          >
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              {label}
            </Typography>
            {href && (
              <Button
                size="small"
                variant="text"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ml: "auto", textTransform: "none" }}
              >
                View
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function OpenButton({ href, label = "View" }) {
  if (!href) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      component={MuiLink}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ textTransform: "none" }}
    >
      {label}
    </Button>
  );
}